import React, { useState } from "react";
import { ADMIN_OPTIONS, DEFAULT_ADMIN } from "../config/admins";
import { getPartnerOrgs, transferUser, OrgNode, OrgUser } from "../api/partner";

interface LookUpProps {
  onBack: () => void;
}

export default function LookUp({ onBack }: LookUpProps) {
  const [adminUuid, setAdminUuid] = useState(DEFAULT_ADMIN.uuid);
  const [loading, setLoading] = useState(false);
  const [orgTree, setOrgTree] = useState<OrgNode | null>(null);
  const [error, setError] = useState("");
  const [transferring, setTransferring] = useState<string | null>(null);
  const [transferTarget, setTransferTarget] = useState<Record<string, string>>({});
  const [feedback, setFeedback] = useState<{ type: "success" | "error"; message: string } | null>(null);

  const handleSearch = async () => {
    setError("");
    setOrgTree(null);
    setFeedback(null);
    setLoading(true);

    try {
      const result = await getPartnerOrgs(adminUuid);
      if (result.ok && result.data) {
        setOrgTree(result.data);
      } else {
        setError(result.message || "Erreur lors de la recherche");
      }
    } catch (err: any) {
      setError(err.message || "Erreur reseau");
    } finally {
      setLoading(false);
    }
  };

  const collectOrgs = (node: OrgNode): { uuid: string; name: string }[] => {
    const orgs = [{ uuid: node.organisation_id, name: node.organisation_name }];
    if (node.sub_organisations) {
      for (const sub of node.sub_organisations) {
        orgs.push(...collectOrgs(sub));
      }
    }
    return orgs;
  };

  const handleTransfer = async (user: OrgUser, fromOrgId: string) => {
    const toOrgId = transferTarget[user.user_id];
    if (!toOrgId) return;

    setFeedback(null);
    setTransferring(user.user_id);

    try {
      const result = await transferUser(fromOrgId, user.user_id, toOrgId);
      if (result.ok) {
        setFeedback({ type: "success", message: `${user.email} a ete transfere avec succes.` });
        setTransferring(null);
        // Refresh the tree
        handleSearch();
      } else {
        setFeedback({ type: "error", message: result.message || "Erreur lors du transfert" });
        setTransferring(null);
      }
    } catch (err: any) {
      setFeedback({ type: "error", message: err.message || "Erreur reseau" });
      setTransferring(null);
    }
  };

  const allOrgs = orgTree ? collectOrgs(orgTree) : [];

  const renderOrg = (org: OrgNode, depth: number = 0) => {
    return (
      <div key={org.organisation_id} style={{ marginLeft: depth * 24, marginBottom: 16 }}>
        <div style={styles.orgHeader}>
          <strong>{org.organisation_name}</strong>
          <span style={styles.orgMeta}>
            {org.nb_users_in_org} utilisateur{org.nb_users_in_org !== 1 ? "s" : ""} | Places restantes : {org.seats_left}
          </span>
        </div>
        {org.users && org.users.length > 0 && (
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Email</th>
                <th style={styles.th}>Role</th>
                <th style={styles.th}>Action</th>
              </tr>
            </thead>
            <tbody>
              {org.users.map((user) => (
                <tr key={user.user_id}>
                  <td style={styles.td}>{user.email}</td>
                  <td style={styles.td}>{user.role}</td>
                  <td style={styles.td}>
                    {user.role === "OWNER" ? (
                      <span style={styles.ownerBadge}>Proprietaire</span>
                    ) : (
                      <div style={styles.transferRow}>
                        <select
                          style={styles.transferSelect}
                          value={transferTarget[user.user_id] || ""}
                          onChange={(e) =>
                            setTransferTarget((prev) => ({
                              ...prev,
                              [user.user_id]: e.target.value,
                            }))
                          }
                        >
                          <option value="">Choisir une org...</option>
                          {allOrgs
                            .filter((o) => o.uuid !== org.organisation_id)
                            .map((o) => (
                              <option key={o.uuid} value={o.uuid}>
                                {o.name}
                              </option>
                            ))}
                        </select>
                        <button
                          style={styles.transferBtn}
                          disabled={
                            !transferTarget[user.user_id] ||
                            transferring === user.user_id
                          }
                          onClick={() => handleTransfer(user, org.organisation_id)}
                        >
                          {transferring === user.user_id
                            ? "..."
                            : "Transferer"}
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {org.sub_organisations &&
          org.sub_organisations.map((sub) => renderOrg(sub, depth + 1))}
      </div>
    );
  };

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <div style={styles.header}>
          <button onClick={onBack} style={styles.backBtn}>
            &larr; Retour
          </button>
          <h2 style={styles.title}>Consulter une organisation</h2>
        </div>

        <div style={styles.searchRow}>
          <select
            value={adminUuid}
            onChange={(e) => setAdminUuid(e.target.value)}
            style={styles.select}
          >
            {ADMIN_OPTIONS.map((admin) => (
              <option key={admin.uuid} value={admin.uuid}>
                {admin.name}
              </option>
            ))}
          </select>
          <button
            onClick={handleSearch}
            disabled={loading}
            style={styles.searchBtn}
          >
            {loading ? "Recherche..." : "Rechercher"}
          </button>
        </div>

        {error && <p style={styles.error}>{error}</p>}
        {feedback && (
          <p style={feedback.type === "success" ? styles.success : styles.error}>
            {feedback.message}
          </p>
        )}

        {orgTree && (
          <div style={styles.treeContainer}>{renderOrg(orgTree)}</div>
        )}
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    display: "flex",
    justifyContent: "center",
    alignItems: "flex-start",
    minHeight: "100vh",
    backgroundColor: "#f5f5f5",
    padding: "40px 16px",
  },
  card: {
    backgroundColor: "#fff",
    borderRadius: 8,
    padding: 40,
    boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
    width: "100%",
    maxWidth: 800,
  },
  header: {
    marginBottom: 24,
  },
  backBtn: {
    background: "none",
    border: "none",
    color: "#4f46e5",
    fontSize: 14,
    cursor: "pointer",
    padding: 0,
    marginBottom: 8,
  },
  title: {
    margin: 0,
    fontSize: 22,
    color: "#333",
  },
  searchRow: {
    display: "flex",
    gap: 12,
    marginBottom: 16,
  },
  select: {
    flex: 1,
    padding: "10px 14px",
    border: "1px solid #ddd",
    borderRadius: 6,
    fontSize: 14,
    backgroundColor: "#fff",
  },
  searchBtn: {
    padding: "10px 20px",
    backgroundColor: "#4f46e5",
    color: "#fff",
    border: "none",
    borderRadius: 6,
    fontSize: 14,
    fontWeight: 600,
    cursor: "pointer",
  },
  treeContainer: {
    marginTop: 16,
  },
  orgHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "8px 12px",
    backgroundColor: "#f3f4f6",
    borderRadius: 6,
    marginBottom: 8,
  },
  orgMeta: {
    fontSize: 12,
    color: "#6b7280",
  },
  table: {
    width: "100%",
    borderCollapse: "collapse" as const,
    marginBottom: 8,
    fontSize: 13,
  },
  th: {
    textAlign: "left" as const,
    padding: "6px 10px",
    borderBottom: "1px solid #e5e7eb",
    color: "#6b7280",
    fontWeight: 600,
    fontSize: 12,
  },
  td: {
    padding: "6px 10px",
    borderBottom: "1px solid #f3f4f6",
  },
  transferRow: {
    display: "flex",
    gap: 6,
    alignItems: "center",
  },
  transferSelect: {
    padding: "4px 8px",
    border: "1px solid #ddd",
    borderRadius: 4,
    fontSize: 12,
    backgroundColor: "#fff",
  },
  transferBtn: {
    padding: "4px 10px",
    backgroundColor: "#4f46e5",
    color: "#fff",
    border: "none",
    borderRadius: 4,
    fontSize: 12,
    cursor: "pointer",
    whiteSpace: "nowrap" as const,
  },
  ownerBadge: {
    fontSize: 11,
    backgroundColor: "#e5e7eb",
    color: "#6b7280",
    padding: "2px 8px",
    borderRadius: 10,
  },
  error: {
    color: "#dc2626",
    fontSize: 13,
    margin: "0 0 12px",
    padding: "8px 12px",
    backgroundColor: "#fef2f2",
    borderRadius: 6,
  },
  success: {
    color: "#16a34a",
    fontSize: 13,
    margin: "0 0 12px",
    padding: "8px 12px",
    backgroundColor: "#f0fdf4",
    borderRadius: 6,
  },
};
